import React, { memo, useMemo } from "react";
import {
  getFretPositions,
  type NeckPosition,
  type NoteName,
} from "../lib/guitarEngine";
import {
  getInstrumentConfig,
  getNoteAtPositionInstrument,
} from "../lib/instrumentEngine";
import { useInstrument } from "../contexts/InstrumentContext";
import { useNeckEditor } from "../contexts/NeckEditorContext";
import {
  chipHexForNeckHighlight,
  exploreWireframeDegreeIndex,
  SCALES_MODULE_ACCENT,
  SERENITY_DEGREE_STROKES,
  SERENITY_TONIC_FILL,
} from "../lib/scaleModuleTheme";
import {
  SERENITY_NECK_ACTIVE_FILL,
  SERENITY_NECK_ACTIVE_FILTER,
  SERENITY_NECK_HOVER_FILL,
  SERENITY_NECK_HOVER_FILTER,
} from "../lib/practiceStaffTheme";

/** Grosor de trazo SVG (px) por número de cuerda — Explore wireframe. */
const EXPLORE_STRING_STROKE: Record<number, number> = {
  6: 3,
  5: 2.5,
  4: 2,
  3: 1.5,
  2: 1,
  1: 0.5,
};

const WIREFRAME_DEGREE_HEX = [
  "#FF4D4D",
  "#FFB4A2",
  "#A8E6CF",
  "#C4B5FD",
  "#F9A8D4",
  "#93C5FD",
  "#DDD6FE",
] as const;

import {
  LUTHERIE_PALETTE,
} from "../lib/fretboardLutherieTheme";

export interface NeckConnection {
  from: { string: number; fret: number };
  to: { string: number; fret: number };
  color?: string;
  label?: string;
  arrow?: boolean;
  dashed?: boolean;
}

interface GuitarNeckProps {
  highlightedPositions?: NeckPosition[];
  mutedStrings?: number[];
  activeNote?: { string: number; fret: number } | null;
  onFretClick?: (stringNum: number, fret: number, note: NoteName) => void;
  onFretHover?: (stringNum: number, fret: number, note: NoteName) => void;
  onFretLeave?: () => void;
  showNoteNames?: boolean;
  showDegrees?: boolean;
  interactive?: boolean;
  stringOrder?: "tab" | "physical"; // tab = 1st at bottom (standard), physical = 6th at bottom
  showFingerBadges?: boolean;
  hand?: "left" | "right";
  hideFretZero?: boolean; // for finger gym: only show frets 1-15
  leftHanded?: boolean;
  barre?: { fromString: number; toString: number; fret: number } | null;
  connections?: NeckConnection[];
  dimmedKeys?: Set<string>; // notas fuera de foco se atenúan
  focusLevel?: 1 | 2 | 3; // 1=Ancla (tónica), 2=Triada, 3=Completa
  practiceFeedback?: "success" | "fail"; // feedback visual en modo práctica
  successPositions?: Set<string>; // notas correctamente seleccionadas en modo aprendizaje
  groupedPositions?: Map<string, { speciesFill: string; groupHalos: string[] }>; // colores por grupo de triada
  /** Cuerdas objetivo resaltadas (sin animación continua). */
  highlightedStrings?: number[];
  /** Ondulación al acertar con el instrumento. */
  ripplingStrings?: number[];
  /** @deprecated Usar highlightedStrings */
  vibratingStrings?: number[];
  /** No dibuja celdas fantasma vacías (menos nodos SVG; gimnasio). */
  sparseCells?: boolean;
  /** `lutherie` (default): diapasón oscuro como Canciones. `warm`: estilo claro legacy. `wireframe`: líneas finas transparentes. */
  visualStyle?: "warm" | "lutherie" | "wireframe";
  /** Explore inmersivo: nodos extra grandes */
  sizeTier?: "default" | "monumental";
  /** Ancho dinámico del diapasón (px) para layout edge-to-edge */
  boardWidthOverride?: number;
  /** Explore 3NPS: solo estas celdas en tamaño completo; el resto de la escala = puntos fantasma */
  spotlightKeys?: Set<string>;
  /** Hover del mástil (pentagrama / preview) sin activar reproducción */
  hoverNote?: { string: number; fret: number } | null;
  /** Durante Play: atenúa notas que no suenan ni reciben hover */
  dimInactiveDuringPlayback?: boolean;
  /** Acordes Explore: O/X tipográfico en cejuela en lugar de nodos grises */
  chordNutTypography?: boolean;
  /** Acordes Explore: nodos ligeramente mayores (w-10 / w-11) */
  exploreChordNodes?: boolean;
}

const GuitarNeckInner: React.FC<GuitarNeckProps> = ({
  highlightedPositions = [],
  mutedStrings = [],
  activeNote,
  onFretClick,
  onFretHover,
  onFretLeave,
  showNoteNames = true,
  showDegrees = true,
  interactive = true,
  stringOrder = "physical",
  showFingerBadges = true,
  hand = "left",
  hideFretZero = false,
  leftHanded = false,
  barre = null,
  connections = [],
  dimmedKeys,
  focusLevel = 3,
  practiceFeedback,
  successPositions,
  groupedPositions,
  highlightedStrings = [],
  ripplingStrings = [],
  vibratingStrings = [],
  sparseCells = false,
  visualStyle = "lutherie",
  sizeTier = "default",
  boardWidthOverride,
  spotlightKeys,
  hoverNote = null,
  dimInactiveDuringPlayback = false,
  chordNutTypography = false,
  exploreChordNodes = false,
}) => {
  const { instrument } = useInstrument();
  const config = getInstrumentConfig(instrument);
  const { values: ed } = useNeckEditor();

  const {
    strings,
    fretCount: frets,
    stringSpacing,
    scaleLength,
    nutWidth,
    dotFrets,
    doubleDotFrets,
    paddingLeft,
    paddingTop,
    targetBoardWidth,
    noteRadius,
    fontScale,
  } = config;

  const stringCount = strings.length;

  // Overrides del editor en vivo
  const liveScaleLength = ed?.scaleLength ?? scaleLength;
  const liveStringSpacing = ed?.stringSpacing ?? stringSpacing;
  const liveNutWidth = ed?.nutWidth ?? nutWidth;
  const livePaddingLeft = ed?.paddingLeft ?? paddingLeft;
  const livePaddingTop = ed?.paddingTop ?? paddingTop;
  const liveTargetBoardWidth = boardWidthOverride ?? ed?.targetBoardWidth ?? targetBoardWidth;
  const liveNoteRadius = ed?.noteRadius ?? noteRadius;
  const liveFontScale = ed?.fontScale ?? fontScale;

  const isWireframe = visualStyle === "wireframe";
  const isLuth = visualStyle === "lutherie" && !isWireframe;
  const isMonumental = sizeTier === "monumental";
  /** Explore inmersivo: wireframe + monumental — nodos compactos con aire entre celdas */
  const isExploreWireframe = isMonumental && isWireframe;

  /** Explore: nodos ~w-9–10 (radio ≤18px) y ≥64px entre cuerdas (h-16). */
  const EXPLORE_NOTE_RADIUS_MAX = exploreChordNodes ? 22 : 18;
  const EXPLORE_STRING_GAP_MIN = 64;

  const noteScale = isExploreWireframe ? 0.92 : isMonumental ? 3.6 : isWireframe ? 2 : 1;
  const spacingScale = isExploreWireframe ? 2.1 : isMonumental ? 3 : isWireframe ? 2.35 : 1.35;

  const zoomStringSpacing = isExploreWireframe
    ? Math.max(liveStringSpacing * spacingScale, EXPLORE_STRING_GAP_MIN)
    : liveStringSpacing * spacingScale;
  const zoomPaddingTop = livePaddingTop * (isExploreWireframe ? 1.45 : isMonumental ? 2 : isWireframe ? 1.75 : 1.6);
  const zoomPaddingLeft = livePaddingLeft * (isExploreWireframe ? 1.4 : isMonumental ? 1.6 : isWireframe ? 1.45 : 1.3);
  const effectiveNoteRadius = isExploreWireframe
    ? Math.min(liveNoteRadius * noteScale, EXPLORE_NOTE_RADIUS_MAX)
    : liveNoteRadius * noteScale;

  /** Etiqueta de cuerda al aire: E A D G B e (una sola letra). */
  const openStringLabelByNum = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of strings) {
      map.set(s.stringNum, s.stringNum === 1 ? s.note.toLowerCase() : s.note);
    }
    return map;
  }, [strings]);

  // Trastes progresivos: traste 1 más ancho que el 15
  const { fretXs, lastFretX } = useMemo(() => {
    const raw = getFretPositions(frets, liveScaleLength);
    const scale = liveTargetBoardWidth / raw[raw.length - 1];
    const xs = raw.map((p) => p * scale);
    return { fretXs: xs, lastFretX: xs[xs.length - 1] };
  }, [frets, liveScaleLength, liveTargetBoardWidth]);

  // Centro de un traste (entre línea fret-1 y línea fret)
  const getFretCenterX = (fret: number) => {
    if (fret === 0) {
      const offset = (fretXs[1] - fretXs[0]) * 0.4;
      return leftHanded ? boardRight + offset : boardLeft - offset;
    }
    const rawX = (fretXs[fret - 1] + fretXs[fret]) / 2;
    return leftHanded ? boardRight - rawX : boardLeft + rawX;
  };

  const boardLeft = zoomPaddingLeft;
  const boardRight = zoomPaddingLeft + lastFretX;

  const totalWidth = zoomPaddingLeft + lastFretX + 80;
  const fretLabelBand = isWireframe ? 36 : 24;
  const totalHeight = zoomPaddingTop * 2 + (stringCount - 1) * zoomStringSpacing + fretLabelBand;

  const highlightStringSet = useMemo(
    () => new Set([...highlightedStrings, ...vibratingStrings]),
    [highlightedStrings, vibratingStrings],
  );
  const ripplingSet = useMemo(() => new Set(ripplingStrings), [ripplingStrings]);

  const highlightMap = useMemo(() => {
    const map = new Map<string, NeckPosition>();
    for (const pos of highlightedPositions) {
      const key = `${pos.string}-${pos.fret}`;
      map.set(key, pos);
    }
    return map;
  }, [highlightedPositions]);

  // String mapping: physical view = 6th at top (grave), 1st at bottom (aguda)
  // tab view = 1st at top, 6th at bottom (like tablature notation)
  const getStringIndex = (visualIndex: number) => {
    if (stringOrder === "physical") {
      // visualIndex 0 = bottom = string 1 (aguda)
      // visualIndex 5 = top = string 6 (grave)
      return visualIndex + 1;
    }
    // tab: visualIndex 0 = bottom = string 6 (grave)
    // visualIndex 5 = top = string 1 (aguda)
    return stringCount - visualIndex;
  };

  const stringLines = useMemo(() => {
    return Array.from({ length: stringCount }, (_, i) => {
      const y = zoomPaddingTop + i * zoomStringSpacing;
      const stringNum = getStringIndex(i);
      // Thicker for lower strings (physically thicker)
      // In physical view: string 1 (aguda) at bottom is thinnest, top string is thickest
      const thickness = stringOrder === "physical"
        ? 1.4 + i * 0.28  // bottom (string 1) thinnest, top thickest
        : 1.4 + (stringCount - 1 - i) * 0.28;  // tab: bottom thickest
      return { y, thickness, stringNum };
    });
  }, [stringOrder, stringCount, zoomPaddingTop, zoomStringSpacing]);

  const fretLines = useMemo(() => {
    return fretXs.map((x, i) => {
      const isNut = i === 0;
      const lineX = leftHanded ? boardRight - x : boardLeft + x;
      return { x: lineX, isNut };
    });
  }, [fretXs, boardLeft, boardRight, leftHanded]);

  const dots = useMemo(() => {
    const dotPositions: { x: number; y1: number; y2?: number }[] = [];
    const centerIdx = (stringCount - 1) / 2;
    for (const fret of dotFrets) {
      if (fret <= frets) {
        const x = getFretCenterX(fret);
        if (doubleDotFrets.includes(fret) && stringCount >= 4) {
          const offset = stringCount >= 6 ? 1 : 0.5;
          dotPositions.push({
            x,
            y1: zoomPaddingTop + (centerIdx - offset) * zoomStringSpacing,
            y2: zoomPaddingTop + (centerIdx + offset) * zoomStringSpacing,
          });
        } else {
          dotPositions.push({ x, y1: zoomPaddingTop + centerIdx * zoomStringSpacing });
        }
      }
    }
    return dotPositions;
  }, [fretXs, zoomPaddingTop, zoomStringSpacing, leftHanded, dotFrets, doubleDotFrets, stringCount, frets]);

  const getNoteId = (stringNum: number, fret: number) => `note-${stringNum}-${fret}`;

  // Finger badge text
  const getFingerBadge = (finger?: number) => {
    if (!finger) return null;
    if (hand === "right") {
      const rightMap: Record<number, string> = { 1: "p", 2: "i", 3: "m", 4: "a" };
      return rightMap[finger] || String(finger);
    }
    return String(finger);
  };

  return (
    <svg
      viewBox={`0 0 ${totalWidth} ${totalHeight}`}
      className="w-full h-full max-w-none select-none"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        {/* Sombras más pronunciadas para perlas */}
        <filter id="pearlShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="rgba(45,42,38,0.22)" />
        </filter>
        <filter id="roseShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="3" stdDeviation="5" floodColor="rgba(255,77,77,0.45)" />
        </filter>
        <filter id="tealShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="3" stdDeviation="5" floodColor="rgba(45,212,191,0.45)" />
        </filter>
        <filter id="sageShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="3" stdDeviation="5" floodColor="rgba(59,130,246,0.45)" />
        </filter>
        <filter id="goldShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="3" stdDeviation="5" floodColor="rgba(249,115,22,0.45)" />
        </filter>
        <filter id="gnLuthDrop" x="-55%" y="-55%" width="210%" height="210%">
          <feDropShadow dx="0" dy="2.5" stdDeviation="3.2" floodColor="#000000" floodOpacity="0.45" />
        </filter>
        <filter id="wfNoteGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="1.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* Inner glow más intenso */}
        <radialGradient id="pearlInner" cx="35%" cy="30%" r="65%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.65)" />
          <stop offset="60%" stopColor="rgba(255,255,255,0.15)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
        <linearGradient id="luthBoardVignette" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.035)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.2)" />
        </linearGradient>
        <radialGradient id="gnLuthShine" cx="32%" cy="28%" r="72%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
          <stop offset="45%" stopColor="rgba(255,255,255,0.12)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
        {isLuth && (
          <>
            <pattern id="gnWoodGrain" width="200" height="200" patternUnits="userSpaceOnUse">
              <rect width="200" height="200" fill={LUTHERIE_PALETTE.fingerboard} />
              <path
                d="M0,20 Q60,15 120,22 T200,18"
                stroke={LUTHERIE_PALETTE.fingerboardLight}
                strokeWidth="0.8"
                fill="none"
                opacity="0.12"
              />
              <path
                d="M0,60 Q80,55 160,62 T200,58"
                stroke={LUTHERIE_PALETTE.fingerboardLight}
                strokeWidth="0.6"
                fill="none"
                opacity="0.1"
              />
              <path
                d="M0,100 Q50,95 100,102 T200,98"
                stroke={LUTHERIE_PALETTE.fingerboardLight}
                strokeWidth="0.7"
                fill="none"
                opacity="0.08"
              />
            </pattern>
            <linearGradient id="gnFingerboardEdge" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={LUTHERIE_PALETTE.fingerboardDark} />
              <stop offset="8%" stopColor={LUTHERIE_PALETTE.fingerboard} />
              <stop offset="92%" stopColor={LUTHERIE_PALETTE.fingerboard} />
              <stop offset="100%" stopColor={LUTHERIE_PALETTE.fingerboardDark} />
            </linearGradient>
            <radialGradient id="gnDotPearl" cx="40%" cy="35%" r="60%">
              <stop offset="0%" stopColor="#FAF6F0" />
              <stop offset="70%" stopColor="#E0D8CC" />
              <stop offset="100%" stopColor="#C8BAA8" />
            </radialGradient>
          </>
        )}
      </defs>

      {/* Diapasón — cálido, luthería o wireframe transparente */}
      {!isWireframe && (
      <>
      <rect
        x={leftHanded ? boardRight - lastFretX - 10 : boardLeft - 10}
        y={zoomPaddingTop - 22}
        width={lastFretX + 20}
        height={(stringCount - 1) * zoomStringSpacing + 48}
        fill={isLuth ? "url(#gnWoodGrain)" : "#F5F0E8"}
        stroke={isLuth ? "rgba(255,255,255,0.06)" : "none"}
        strokeWidth={isLuth ? 1 : 0}
        rx={16}
      />
      {isLuth && (
        <rect
          x={leftHanded ? boardRight - lastFretX - 10 : boardLeft - 10}
          y={zoomPaddingTop - 22}
          width={lastFretX + 20}
          height={(stringCount - 1) * zoomStringSpacing + 48}
          fill="url(#gnFingerboardEdge)"
          rx={16}
          opacity={0.28}
        />
      )}
      {isLuth && (
        <rect
          x={leftHanded ? boardRight - lastFretX - 10 : boardLeft - 10}
          y={zoomPaddingTop - 22}
          width={lastFretX + 20}
          height={(stringCount - 1) * zoomStringSpacing + 48}
          fill="url(#luthBoardVignette)"
          rx={16}
          opacity={0.85}
        />
      )}
      </>
      )}

      {/* Cejuela */}
      {isWireframe ? (
        <line
          x1={leftHanded ? boardRight - liveNutWidth / 2 : boardLeft - liveNutWidth / 2}
          y1={zoomPaddingTop - 16}
          x2={leftHanded ? boardRight - liveNutWidth / 2 : boardLeft - liveNutWidth / 2}
          y2={zoomPaddingTop + (stringCount - 1) * zoomStringSpacing + 16}
          stroke="rgba(255,255,255,0.35)"
          strokeWidth={2}
          strokeLinecap="round"
        />
      ) : (
      <rect
        x={leftHanded ? boardRight - liveNutWidth / 2 - 3 : boardLeft - liveNutWidth / 2 - 3}
        y={zoomPaddingTop - 18}
        width={liveNutWidth + 4}
        height={(stringCount - 1) * zoomStringSpacing + 40}
        fill={isLuth ? LUTHERIE_PALETTE.nut : "#FAF3EB"}
        rx={3}
        stroke={isLuth ? LUTHERIE_PALETTE.nutShadow : "#E0D5C8"}
        strokeWidth={0.8}
        opacity={isLuth ? 0.95 : 0.8}
      />
      )}

      {/* Trastes */}
      {fretLines.map(({ x, isNut }, i) => (
        <line
          key={`fret-${i}`}
          x1={x}
          y1={zoomPaddingTop - 16}
          x2={x}
          y2={zoomPaddingTop + (stringCount - 1) * zoomStringSpacing + 16}
          stroke={
            isExploreWireframe
              ? "rgba(255,255,255,0.08)"
              : isWireframe
              ? "rgba(255,255,255,0.22)"
              : isNut
              ? isLuth
                ? LUTHERIE_PALETTE.fretWireHighlight
                : "#C4B5A5"
              : isLuth
              ? LUTHERIE_PALETTE.fretWire
              : "#D4C8B8"
          }
          strokeWidth={isWireframe ? 1 : isNut ? liveNutWidth + 1 : isLuth ? 1.1 : 0.8}
          opacity={
            isExploreWireframe ? (isNut ? 0.35 : 0.22) : isWireframe ? (isNut ? 0.55 : 0.42) : isNut ? 0.95 : isLuth ? 0.55 : 0.45
          }
          strokeLinecap="round"
        />
      ))}

      {/* Marcadores de posición (inlays 3·5·7·9·12) */}
      {dots.map((dot, i) => (
        <g key={`dot-${i}`}>
          <circle
            cx={dot.x}
            cy={dot.y1}
            r={isExploreWireframe ? 8 : isWireframe ? 3 : 4}
            fill={isExploreWireframe ? "rgba(255,255,255,0.1)" : isWireframe ? "rgba(255,255,255,0.15)" : isLuth ? "url(#gnDotPearl)" : "#C4B5A0"}
            opacity={isExploreWireframe ? 1 : isWireframe ? 0.4 : isLuth ? 0.65 : 0.25}
          />
          {dot.y2 && (
            <circle
              cx={dot.x}
              cy={dot.y2}
              r={isExploreWireframe ? 8 : isWireframe ? 3 : 4}
              fill={isExploreWireframe ? "rgba(255,255,255,0.1)" : isWireframe ? "rgba(255,255,255,0.15)" : isLuth ? "url(#gnDotPearl)" : "#C4B5A0"}
              opacity={isExploreWireframe ? 1 : isWireframe ? 0.4 : isLuth ? 0.65 : 0.25}
            />
          )}
        </g>
      ))}

      {/* Etiquetas de cuerdas al aire — una letra por fila (E A D G B e) */}
      {isExploreWireframe &&
        stringLines.map(({ y, stringNum }) => {
          const label = openStringLabelByNum.get(stringNum) ?? "";
          const labelX = leftHanded ? boardRight + 36 : boardLeft - 44;
          return (
            <text
              key={`str-label-${stringNum}`}
              x={labelX}
              y={y + 4}
              textAnchor={leftHanded ? "start" : "end"}
              fontSize={14}
              fill="rgba(255,255,255,0.4)"
              fontFamily="'JetBrains Mono', 'SF Mono', Menlo, monospace"
              fontWeight="500"
            >
              {label}
            </text>
          );
        })}

      {/* Números de trastes (0–{frets}) */}
      {Array.from({ length: (hideFretZero ? frets : frets + 1) }, (_, i) => {
        const fretNum = hideFretZero ? i + 1 : i;
        const x = getFretCenterX(fretNum);
        return (
          <text
            key={`fret-num-${fretNum}`}
            x={x}
            y={totalHeight - 10}
            textAnchor="middle"
            fontSize={isWireframe ? 14 : 11}
            fill={isWireframe ? "rgba(255,255,255,0.5)" : isLuth ? "rgba(200,210,218,0.5)" : "#B8A99A"}
            fontFamily="'Inter', sans-serif"
            fontWeight="400"
          >
            {fretNum}
          </text>
        );
      })}

      {/* Cuerdas */}
      {stringLines.map(({ y, stringNum, thickness }) => {
        const wound = stringNum >= 4;
        const isHighlighted = highlightStringSet.has(stringNum);
        const isRippling = ripplingSet.has(stringNum);
        if (isWireframe) {
          const x1 = leftHanded ? boardLeft : boardLeft - liveNutWidth / 2;
          const x2 = leftHanded ? boardRight + liveNutWidth / 2 : boardRight;
          const muted = mutedStrings.includes(stringNum);
          const strokeW = isExploreWireframe
            ? (EXPLORE_STRING_STROKE[stringNum] ?? 1.5)
            : 2;
          return (
            <line
              key={`line-${stringNum}`}
              x1={x1}
              y1={y}
              x2={x2}
              y2={y}
              stroke={
                muted
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(255, 255, 255, 0.2)"
              }
              strokeWidth={strokeW}
              strokeDasharray={muted ? "4,3" : undefined}
              strokeLinecap="round"
              opacity={muted ? 0.3 : stringNum === 1 && isExploreWireframe ? 0.5 : 0.85}
            />
          );
        }
        if (isLuth) {
          const x1 = leftHanded ? boardLeft : boardLeft - liveNutWidth / 2;
          const x2 = leftHanded ? boardRight + liveNutWidth / 2 : boardRight;
          const core = wound ? "rgba(210,150,95,0.42)" : "rgba(255,255,255,0.14)";
          const rim = wound ? thickness * 1.65 : thickness * 0.9;
          const muted = mutedStrings.includes(stringNum);
          const strokeCore = isHighlighted
            ? wound
              ? "rgba(255,210,140,0.92)"
              : "rgba(255,255,255,0.88)"
            : muted
              ? "#6B6560"
              : core;
          return (
            <g key={`line-${stringNum}`}>
              {isHighlighted && !isRippling && (
                <line
                  x1={x1}
                  y1={y}
                  x2={x2}
                  y2={y}
                  stroke={wound ? "rgba(236,72,153,0.45)" : "rgba(255,255,255,0.28)"}
                  strokeWidth={rim * 2.4}
                  strokeLinecap="round"
                  opacity={0.85}
                />
              )}
              {isRippling && (
                <>
                  <line
                    x1={x1}
                    y1={y}
                    x2={x2}
                    y2={y}
                    stroke={wound ? "rgba(255,200,120,0.55)" : "rgba(255,255,255,0.4)"}
                    strokeWidth={rim * 3.2}
                    strokeLinecap="round"
                    style={{
                      transformOrigin: `${(x1 + x2) / 2}px ${y}px`,
                      animation: "gnStringRipplePulse 0.45s ease-in-out 3",
                    }}
                  />
                  <line
                    x1={x1}
                    y1={y}
                    x2={x2}
                    y2={y}
                    stroke={wound ? "#FFD080" : "#FFFFFF"}
                    strokeWidth={rim * 1.1}
                    strokeLinecap="round"
                    strokeDasharray="8 14"
                    style={{ animation: "gnStringRipple 0.7s linear 3" }}
                  />
                </>
              )}
              <line
                x1={x1}
                y1={y + 1.2}
                x2={x2}
                y2={y + 1.2}
                stroke="rgba(0,0,0,0.55)"
                strokeWidth={thickness * 1.25}
                opacity={muted ? 0.25 : 0.45}
                strokeLinecap="round"
                strokeDasharray={muted ? "5,4" : "none"}
              />
              <line
                x1={x1}
                y1={y}
                x2={x2}
                y2={y}
                stroke={strokeCore}
                strokeWidth={muted ? 1 : rim}
                strokeLinecap="round"
                strokeDasharray={muted ? "5,4" : "none"}
                opacity={muted ? 0.45 : 0.88}
              />
              {!muted && !wound && (
                <line
                  x1={x1}
                  y1={y - 0.35}
                  x2={x2}
                  y2={y - 0.35}
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth={Math.max(0.45, thickness * 0.35)}
                  strokeLinecap="round"
                  opacity={0.9}
                />
              )}
              {!muted && wound && (
                <line
                  x1={x1}
                  y1={y}
                  x2={x2}
                  y2={y}
                  stroke="rgba(240,190,130,0.22)"
                  strokeWidth={Math.max(1.1, rim * 0.55)}
                  strokeLinecap="round"
                  strokeDasharray="0.4,2.2"
                  opacity={0.85}
                />
              )}
            </g>
          );
        }
        return (
          <line
            key={`line-${stringNum}`}
            x1={leftHanded ? boardLeft : boardLeft - liveNutWidth / 2}
            y1={y}
            x2={leftHanded ? boardRight + liveNutWidth / 2 : boardRight}
            y2={y}
            stroke={
              mutedStrings.includes(stringNum)
                ? "#B8A99A"
                : "#C4B8A8"
            }
            strokeWidth={mutedStrings.includes(stringNum) ? 1 : thickness}
            strokeDasharray={mutedStrings.includes(stringNum) ? "5,4" : "none"}
            opacity={mutedStrings.includes(stringNum) ? 0.5 : 0.55}
            strokeLinecap="round"
          />
        );
      })}

      {/* Cejilla (barre) */}
      {barre && barre.fret > 0 && (() => {
        const fromIdx = stringLines.findIndex((sl) => sl.stringNum === barre.fromString);
        const toIdx = stringLines.findIndex((sl) => sl.stringNum === barre.toString);
        if (fromIdx === -1 || toIdx === -1) return null;
        const y1 = stringLines[Math.min(fromIdx, toIdx)].y;
        const y2 = stringLines[Math.max(fromIdx, toIdx)].y;
        const cx = getFretCenterX(barre.fret);
        const barreWidth = 18;
        return (
          <rect
            x={cx - barreWidth / 2}
            y={y1 - 8}
            width={barreWidth}
            height={y2 - y1 + 16}
            rx={6}
            fill="#7B2335"
            opacity={0.35}
          />
        );
      })()}

      {/* Rutas mnemotécnicas — arcos que vuelan por encima del mástil */}
      <style>{`
        @keyframes drawRoute {
          to { stroke-dashoffset: 0; }
        }
        @keyframes noteBloom {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); }
        }
        @keyframes haloSuccess {
          0% { stroke-width: 2; stroke-opacity: 0.8; r: ${liveNoteRadius * 1.35 + 7}; }
          100% { stroke-width: 0; stroke-opacity: 0; r: ${liveNoteRadius * 1.35 + 28}; }
        }
        @keyframes successBloom {
          0% { transform: scale(0.5); opacity: 0; }
          60% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); }
        }
        @keyframes shakeFail {
          0%, 100% { transform: translate(0, 0); }
          20% { transform: translate(-3px, 0); }
          40% { transform: translate(3px, 0); }
          60% { transform: translate(-2px, 0); }
          80% { transform: translate(2px, 0); }
        }
        @keyframes gnStringRipple {
          0% { stroke-dashoffset: 0; opacity: 0.95; }
          100% { stroke-dashoffset: -56; opacity: 0.15; }
        }
        @keyframes gnStringRipplePulse {
          0%, 100% { opacity: 0.25; transform: scaleY(1); }
          50% { opacity: 0.75; transform: scaleY(1.35); }
        }
      `}</style>
      {connections.map((conn, ci) => {
        const fromLine = stringLines.find((sl) => sl.stringNum === conn.from.string);
        const toLine = stringLines.find((sl) => sl.stringNum === conn.to.string);
        if (!fromLine || !toLine) return null;
        const x1 = getFretCenterX(conn.from.fret);
        const y1 = fromLine.y;
        const x2 = getFretCenterX(conn.to.fret);
        const y2 = toLine.y;
        const color = conn.color || SCALES_MODULE_ACCENT;

        // Arco alto: punto de control va ARRIBA del mástil
        const midX = (x1 + x2) / 2;
        const span = Math.abs(x2 - x1);
        const archHeight = Math.min(span * 0.35 + 20, 90);
        const ctrlX = midX;
        const ctrlY = Math.min(y1, y2) - archHeight;
        const pathD = `M ${x1} ${y1} Q ${ctrlX} ${ctrlY} ${x2} ${y2}`;

        // Opacidad por distancia: lejos = más transparente
        const dist = Math.hypot(x2 - x1, y2 - y1);
        const lineOpacity = Math.max(0.15, 0.7 - dist / 400);

        // Flecha al final
        const t = 0.94;
        const bx = (1 - t) * (1 - t) * x1 + 2 * (1 - t) * t * ctrlX + t * t * x2;
        const by = (1 - t) * (1 - t) * y1 + 2 * (1 - t) * t * ctrlY + t * t * y2;
        const angle = Math.atan2(y2 - by, x2 - bx);
        const arrowSize = 5;
        const ax1 = x2 - arrowSize * Math.cos(angle - Math.PI / 5);
        const ay1 = y2 - arrowSize * Math.sin(angle - Math.PI / 5);
        const ax2 = x2 - arrowSize * Math.cos(angle + Math.PI / 5);
        const ay2 = y2 - arrowSize * Math.sin(angle + Math.PI / 5);

        // Longitud estimada del path para animación
        const pathLen = span + archHeight * 2;

        return (
          <g key={`conn-${ci}`}>
            {/* Halo difuso */}
            <path
              d={pathD}
              fill="none"
              stroke={color}
              strokeWidth={6}
              strokeLinecap="round"
              opacity={lineOpacity * 0.12}
            />
            {/* Arco principal con animación de trazado */}
            <path
              d={pathD}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeLinecap="round"
              opacity={lineOpacity}
              strokeDasharray={pathLen}
              strokeDashoffset={pathLen}
              style={{
                animation: `drawRoute 1.2s ease ${ci * 0.15}s forwards`,
              }}
            >
              {conn.label && <title>{conn.label}</title>}
            </path>
            {conn.arrow !== false && (
              <polygon
                points={`${x2},${y2} ${ax1},${ay1} ${ax2},${ay2}`}
                fill={color}
                opacity={lineOpacity + 0.2}
                style={{
                  animation: `drawRoute 0.4s ease ${ci * 0.15 + 0.8}s forwards`,
                  opacity: 0,
                }}
              />
            )}
          </g>
        );
      })}

      {/* Notas resaltadas */}
      {Array.from({ length: stringCount }, (_, vi) => {
        const stringNum = getStringIndex(vi);
        const y = zoomPaddingTop + vi * zoomStringSpacing;

        return Array.from({ length: frets + 1 }, (_, fi) => {
          const fret = fi;
          if (hideFretZero && fret === 0) return null;

          const key = `${stringNum}-${fret}`;
          const pos = highlightMap.get(key);
          const isNutChordTypography =
            chordNutTypography && isExploreWireframe && fret === 0;
          const isActive = activeNote?.string === stringNum && activeNote?.fret === fret;
          const isHovered =
            hoverNote?.string === stringNum && hoverNote?.fret === fret && !isActive;
          const isLit = isActive || isHovered;
          const note = getNoteAtPositionInstrument(stringNum, fret, instrument);
          const isMuted = mutedStrings.includes(stringNum);
          const isDimmed = dimmedKeys && !dimmedKeys.has(key) && !isActive;
          const isSuccess = successPositions?.has(key);
          const groupInfo = groupedPositions?.get(key);
          const isGrouped = !!groupInfo;

          // Siempre renderizamos la nota (fantasma si no esta en la escala)
          const cx = getFretCenterX(fret);
          const baseRadius = fret === 0 ? effectiveNoteRadius - 2 : effectiveNoteRadius;
          const radius = isSuccess || isGrouped ? baseRadius * 1.3 : baseRadius;

          let fill = isWireframe ? "rgba(255,255,255,0.05)" : isLuth ? "rgba(255,255,255,0.04)" : "#D8CFC4";
          let stroke = isWireframe ? "rgba(255,255,255,0.12)" : isLuth ? "rgba(255,255,255,0.14)" : "#C4B8A8";
          let textColor = isWireframe ? "rgba(255,255,255,0.35)" : isLuth ? "rgba(220,225,232,0.72)" : "#9A8B7A";
          let opacity = isWireframe ? 0.35 : 0.45;
          let isGhost = true;

          if (isGrouped) {
            isGhost = false;
            fill = groupInfo.speciesFill; stroke = "#FFFFFF"; textColor = "#FFFFFF";
            opacity = 1;
          } else if (isSuccess) {
            isGhost = false;
            fill = "#22C55E"; stroke = "#FFFFFF"; textColor = "#FFFFFF";
            opacity = 1;
          } else if (pos) {
            isGhost = false;
            if (isWireframe) {
              const degIdx = exploreWireframeDegreeIndex(pos.degree);
              const isRootOrActive =
                pos.highlight === "root" ||
                pos.highlight === "active" ||
                isActive ||
                pos.degree === "R";
              if (isExploreWireframe) {
                if (isActive) {
                  fill = SERENITY_NECK_ACTIVE_FILL;
                  stroke = "rgba(255,255,255,0.5)";
                  textColor = "#FFFFFF";
                  opacity = isMuted ? 0.35 : 1;
                } else if (isHovered) {
                  fill = SERENITY_NECK_HOVER_FILL;
                  stroke = "rgba(255,255,255,0.5)";
                  textColor = "#FFFFFF";
                  opacity = isMuted ? 0.35 : 0.95;
                } else if (isRootOrActive) {
                  fill = SERENITY_TONIC_FILL;
                  stroke = "rgba(255,255,255,0.55)";
                  textColor = "#FFFFFF";
                  opacity = isMuted ? 0.22 : isDimmed ? 0.12 : 1;
                } else {
                  fill = "rgba(255,255,255,0.08)";
                  stroke = SERENITY_DEGREE_STROKES[degIdx % 7];
                  textColor = "rgba(255,255,255,0.82)";
                  opacity = isMuted ? 0.2 : isDimmed ? 0.1 : 0.88;
                }
              } else {
                fill = isRootOrActive ? "#FF4D4D" : WIREFRAME_DEGREE_HEX[degIdx % 7];
                stroke = fill;
                textColor = "#FFFFFF";
                opacity = isMuted ? 0.22 : isDimmed ? 0.12 : isRootOrActive ? 1 : 0.52;
              }
            } else {
              fill = chipHexForNeckHighlight(pos.highlight, pos.degree);
              stroke = "#FFFFFF";
              textColor = "#FFFFFF";
              opacity = isMuted ? 0.35 : isDimmed ? 0.2 : 1;
            }
          }

          if (isMuted) { fill = "#B8A99A"; stroke = "#9A8B7C"; textColor = "#FFFFFF"; opacity = 0.35; }

          if (sparseCells && isGhost && !pos && !isActive && !isSuccess && !isGrouped) {
            return null;
          }

          if (isNutChordTypography) {
            const cx = getFretCenterX(0);
            if (isMuted) {
              return (
                <text
                  key={getNoteId(stringNum, fret)}
                  x={cx}
                  y={y + 5}
                  textAnchor="middle"
                  fontSize={18 * liveFontScale}
                  fill="rgba(255,255,255,0.2)"
                  fontFamily="'JetBrains Mono', 'SF Mono', Menlo, monospace"
                  fontWeight="500"
                  style={{ pointerEvents: interactive ? "auto" : "none", cursor: interactive ? "pointer" : "default" }}
                  onClick={() => interactive && onFretClick?.(stringNum, fret, note.note)}
                >
                  X
                </text>
              );
            }
            if (pos) {
              const isRoot = pos.degree === "R" || pos.highlight === "root";
              return (
                <g
                  key={getNoteId(stringNum, fret)}
                  style={{ cursor: interactive ? "pointer" : "default" }}
                  onClick={() => interactive && onFretClick?.(stringNum, fret, note.note)}
                  onMouseEnter={() => interactive && onFretHover?.(stringNum, fret, note.note)}
                  onMouseLeave={() => interactive && onFretLeave?.()}
                >
                  <text
                    x={cx}
                    y={y + 6}
                    textAnchor="middle"
                    fontSize={20 * liveFontScale}
                    fill={isRoot ? "#f0a8a0" : "#34d399"}
                    fontFamily="'JetBrains Mono', 'SF Mono', Menlo, monospace"
                    fontWeight="600"
                    style={{
                      filter: isRoot
                        ? "drop-shadow(0 0 10px rgba(240,168,160,0.7))"
                        : "drop-shadow(0 0 8px rgba(52,211,153,0.65))",
                    }}
                  >
                    O
                  </text>
                </g>
              );
            }
            return null;
          }

          const inSpotlight = !spotlightKeys || spotlightKeys.has(key);
          const isSpotlightGhost =
            spotlightKeys && pos && !inSpotlight && !isActive && !isSuccess && !isGrouped;

          if (isSpotlightGhost) {
            if (isExploreWireframe) {
              return (
                <g
                  key={getNoteId(stringNum, fret)}
                  style={{ cursor: interactive ? "pointer" : "default" }}
                  onClick={() => interactive && onFretClick?.(stringNum, fret, note.note)}
                >
                  <circle
                    cx={cx}
                    cy={y}
                    r={effectiveNoteRadius * 0.55}
                    fill="rgba(255,255,255,0.06)"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth={0.75}
                    opacity={0.2}
                  />
                </g>
              );
            }
            return (
              <g
                key={getNoteId(stringNum, fret)}
                style={{ cursor: interactive ? "pointer" : "default" }}
                onClick={() => interactive && onFretClick?.(stringNum, fret, note.note)}
              >
                <circle
                  cx={cx}
                  cy={y}
                  r={3.5}
                  fill="rgba(255,255,255,0.08)"
                  stroke="rgba(255,255,255,0.12)"
                  strokeWidth={0.5}
                  opacity={0.65}
                />
              </g>
            );
          }

          // Modo Focus: filtrar que notas se muestran brillantes
          const isRoot = pos?.highlight === "root" || pos?.degree === "R";
          const isThird = pos?.highlight === "third" || pos?.degree === "3" || pos?.degree === "b3";
          const isFifth = pos?.highlight === "fifth" || pos?.degree === "5";
          if (focusLevel === 1 && pos && !isRoot && !isSuccess && !isGrouped) {
            opacity = 0.12;
          } else if (focusLevel === 2 && pos && !isRoot && !isThird && !isFifth && !isSuccess && !isGrouped) {
            opacity = 0.12;
          }

          if (
            isExploreWireframe &&
            dimInactiveDuringPlayback &&
            pos &&
            !isLit &&
            !isSuccess &&
            !isGrouped
          ) {
            opacity = Math.min(opacity, 0.5);
          }

          const exploreNodeStyle: React.CSSProperties | undefined = isExploreWireframe
            ? {
                transformOrigin: `${cx}px ${y}px`,
                transition: "all 300ms ease-out",
                transform: isLit ? "scale(1.1)" : "scale(1)",
                filter: isActive
                  ? SERENITY_NECK_ACTIVE_FILTER
                  : isHovered
                    ? SERENITY_NECK_HOVER_FILTER
                    : undefined,
              }
            : undefined;

          const badge = showFingerBadges !== false && pos?.finger ? getFingerBadge(pos?.finger) : null;

          return (
            <g
              key={getNoteId(stringNum, fret)}
              id={getNoteId(stringNum, fret)}
              style={{ cursor: interactive ? "pointer" : "default" }}
              onClick={() => interactive && onFretClick?.(stringNum, fret, note.note)}
              onMouseEnter={() => interactive && onFretHover?.(stringNum, fret, note.note)}
              onMouseLeave={() => interactive && onFretLeave?.()}
            >
              {/* Halo activo + feedback de práctica (Explore usa drop-shadow en el nodo) */}
              {isActive && !isExploreWireframe && (
                <g>
                  <circle cx={cx} cy={y} r={radius + 7} fill="none" stroke={SCALES_MODULE_ACCENT} strokeWidth={2} opacity={0.38} />
                  {practiceFeedback === "success" && (
                    <circle
                      cx={cx} cy={y}
                      fill="none"
                      stroke="#22C55E"
                      opacity={0.9}
                      style={{
                        animation: `haloSuccess 0.6s ease-out forwards`,
                      }}
                    />
                  )}
                </g>
              )}

              {/* Halo de éxito para notas descubiertas correctamente */}
              {isSuccess && (
                <g>
                  <circle
                    cx={cx} cy={y}
                    r={radius + 10}
                    fill="none"
                    stroke="#22C55E"
                    strokeWidth={3}
                    opacity={0.5}
                    style={{
                      animation: `haloSuccess 0.6s ease-out forwards`,
                      transformOrigin: `${cx}px ${y}px`,
                    }}
                  />
                </g>
              )}
              {/* Halos de grupo (uno por grupo al que pertenece la nota) */}
              {isGrouped && groupInfo.groupHalos.map((haloColor, hi) => (
                <g key={`halo-${hi}`}>
                  <circle
                    cx={cx} cy={y}
                    r={radius + 10 + hi * 5}
                    fill="none"
                    stroke={haloColor}
                    strokeWidth={4}
                    opacity={0.75}
                    style={{
                      animation: `haloSuccess 0.6s ease-out forwards`,
                      transformOrigin: `${cx}px ${y}px`,
                    }}
                  />
                </g>
              ))}

              {/* Circulo principal — cálido perla o esfera saturada (lutherie = Canciones) */}
              {isGhost ? (
                <circle
                  cx={cx} cy={y} r={radius * (isWireframe ? 0.45 : isLuth ? 0.52 : 0.5)}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={isWireframe ? 0.5 : isLuth ? 1 : 0.8}
                  opacity={opacity}
                />
              ) : isWireframe ? (
                <g
                  style={{
                    transformOrigin: `${cx}px ${y}px`,
                    animation:
                      isExploreWireframe || isLit
                        ? undefined
                        : isSuccess
                          ? `successBloom 0.5s ease-out forwards`
                          : `noteBloom 0.5s ease-out ${(stringNum * 3 + fret) * 0.02}s both`,
                    ...(practiceFeedback === "fail" && isActive
                      ? { animation: `shakeFail 0.4s ease-in-out` }
                      : {}),
                    ...exploreNodeStyle,
                  }}
                >
                  {!isExploreWireframe &&
                    (isActive || pos?.highlight === "root" || pos?.highlight === "active") && (
                      <circle
                        cx={cx}
                        cy={y}
                        r={radius * 1.22}
                        fill="none"
                        stroke={fill}
                        strokeWidth={1.5}
                        opacity={0.35}
                      />
                    )}
                  <circle
                    cx={cx}
                    cy={y}
                    r={radius * (isLit ? 1.08 : 1)}
                    fill={fill}
                    stroke={
                      isLit || pos?.highlight === "root" || pos?.highlight === "active"
                        ? "rgba(255,255,255,0.5)"
                        : stroke
                    }
                    strokeWidth={isLit || pos?.highlight === "root" ? 1.5 : 1}
                    opacity={opacity}
                  />
                </g>
              ) : isLuth ? (
                <g
                  style={{
                    transformOrigin: `${cx}px ${y}px`,
                    animation: isSuccess
                      ? `successBloom 0.5s ease-out forwards`
                      : `noteBloom 0.5s ease-out ${(stringNum * 3 + fret) * 0.02}s both`,
                    ...(practiceFeedback === "fail" && isActive
                      ? { animation: `shakeFail 0.4s ease-in-out` }
                      : {}),
                  }}
                >
                  <circle
                    cx={cx} cy={y} r={radius * 1.4}
                    fill="none"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth={1}
                    opacity={opacity}
                  />
                  <circle
                    cx={cx} cy={y} r={radius * 1.35}
                    fill={fill}
                    stroke={
                      practiceFeedback === "fail" && isActive
                        ? "#E8486E"
                        : isActive
                          ? "rgba(255,255,255,0.95)"
                          : "rgba(255,255,255,0.38)"
                    }
                    strokeWidth={
                      practiceFeedback === "fail" && isActive ? 4 : isActive ? 2.15 : 1.4
                    }
                    opacity={opacity * (isActive ? 1 : 0.94)}
                    filter="url(#gnLuthDrop)"
                  />
                  <circle
                    cx={cx} cy={y} r={radius * 1.32}
                    fill="url(#gnLuthShine)"
                    opacity={0.48}
                    pointerEvents="none"
                  />
                </g>
              ) : (
                <g
                  style={{
                    transformOrigin: `${cx}px ${y}px`,
                    animation: isSuccess
                      ? `successBloom 0.5s ease-out forwards`
                      : `noteBloom 0.5s ease-out ${(stringNum * 3 + fret) * 0.02}s both`,
                    ...(practiceFeedback === "fail" && isActive
                      ? { animation: `shakeFail 0.4s ease-in-out` }
                      : {}),
                  }}
                >
                  <circle
                    cx={cx} cy={y} r={radius * 1.35}
                    fill={fill}
                    stroke={practiceFeedback === "fail" && isActive ? "#E8486E" : stroke}
                    strokeWidth={practiceFeedback === "fail" && isActive ? 4 : 2.5}
                    opacity={opacity}
                    filter={
                      pos?.highlight === "root" || pos?.highlight === "active"
                        ? "url(#roseShadow)"
                        : pos?.highlight === "third"
                          ? "url(#tealShadow)"
                          : pos?.highlight === "fifth"
                            ? "url(#sageShadow)"
                            : "url(#goldShadow)"
                    }
                  />
                  <circle
                    cx={cx} cy={y} r={radius * 1.35}
                    fill="url(#pearlInner)"
                    opacity={0.6}
                    pointerEvents="none"
                  />
                </g>
              )}

              {/* X para mute */}
              {isMuted && (
                <g opacity={0.7}>
                  <line x1={cx - 6} y1={y - 6} x2={cx + 6} y2={y + 6} stroke="#FFF" strokeWidth={2} />
                  <line x1={cx + 6} y1={y - 6} x2={cx - 6} y2={y + 6} stroke="#FFF" strokeWidth={2} />
                </g>
              )}

              {/* Nombre de nota */}
              {!isMuted && showNoteNames && !(isExploreWireframe && fret === 0) && (
                <text
                  x={cx} y={y + (isGhost ? 3 : isWireframe ? 4 : 4)}
                  textAnchor="middle"
                  fontSize={
                    isExploreWireframe
                      ? (isGhost ? 10 : 15) * liveFontScale
                      : isMonumental
                      ? (isGhost ? 12 : 22) * liveFontScale
                      : isWireframe
                      ? (isGhost ? 10 : 16) * liveFontScale
                      : (isGhost ? 9 : 13) * liveFontScale
                  }
                  fill={textColor}
                  fontWeight={
                    isExploreWireframe && !isGhost ? "500" : isMonumental || (isWireframe && !isGhost) ? "700" : isGhost ? "400" : "600"
                  }
                  fontFamily={isExploreWireframe ? "'Inter', sans-serif" : "'Inter', sans-serif"}
                  style={{ pointerEvents: "none" }}
                >
                  {pos?.note || note.note}
                </text>
              )}

              {/* Dedo — firma monoespaciada fina en la base del círculo */}
              {!isGhost && !isMuted && badge && (
                <text
                  x={cx}
                  y={y + radius * 1.35 - 3}
                  textAnchor="middle"
                  fontSize={7 * liveFontScale}
                  fill="#FFFFFF"
                  fontWeight="400"
                  fontFamily="'SF Mono', 'Menlo', 'Courier New', monospace"
                  opacity={0.85}
                  style={{ pointerEvents: "none" }}
                >
                  {badge}
                </text>
              )}

              {/* Grado del acorde — más sutil */}
              {!isMuted && showDegrees && pos?.degree && (
                <text
                  x={cx} y={y - radius * 1.35 - 6}
                  textAnchor="middle"
                  fontSize={9 * liveFontScale}
                  fill={isGhost ? (isLuth ? "rgba(200,210,218,0.5)" : "#B8A99A") : isLuth ? "rgba(255,255,255,0.88)" : "#8A7A6A"}
                  fontWeight="600"
                  fontFamily="'Inter', sans-serif"
                  style={{ pointerEvents: "none" }}
                >
                  {pos.degree}
                </text>
              )}
            </g>
          );
        });
      })}
    </svg>
  );
};

export const GuitarNeck = memo(GuitarNeckInner);
export default GuitarNeck;
